import { ComponentSingleStyleConfig } from '@chakra-ui/react';
import type { StyleFunctionProps, SystemStyleObject } from '@chakra-ui/theme-tools';
import { getColor } from '@chakra-ui/theme-tools';

import { pickTextColorBasedOnBgColor, getOneStepLighterColor, getOneStepDarkerColor } from './utils';

import { convertHexToRGB } from '_helpers';
import SEMANTIC_TOKENS from 'theme/semanticTokens';
import { getElevatedShadow, getFocusedOutlineShadow } from 'theme/shadows';

export const iconSizeMap = {
  xs: '12px',
  sm: '16px',
  md: '20px',
  lg: '24px'
};

export const iconSpacingMap = {
  xs: '4px',
  sm: '6px',
  md: '8px',
  lg: '10px'
};

export const iconPaddingMap = {
  xs: '6px',
  sm: '8px',
  md: '12px',
  lg: '12px'
};

const baseStyle = (props: StyleFunctionProps): SystemStyleObject => ({
  cursor: 'pointer',
  fontWeight: 'medium',
  lineHeight: 1.5,
  boxShadow: getElevatedShadow(props),
  _disabled: {
    opacity: 1,
    cursor: 'not-allowed',
    boxShadow: 'none',
    bg: 'silver.light',
    color: 'grey.light',
    svg: {
      color: 'grey.light'
    }
  },
  _focus: {
    boxShadow: getFocusedOutlineShadow(props)
  },
  _hover: {
    _disabled: {
      bg: 'silver.light'
    }
  },
  _dark: {
    _disabled: {
      color: 'grey.light',
      svg: {
        color: 'grey.light'
      }
    }
  }
});

const primary = (props: StyleFunctionProps): SystemStyleObject => {
  let { colorScheme: c } = props;
  const { color = 'silver.lightest' } = props;
  if (c === 'gray') c = 'primary';
  const hoverBg = getOneStepDarkerColor(SEMANTIC_TOKENS.colors[c].default);
  const activeBg = getOneStepDarkerColor(hoverBg);

  return {
    bg: c,
    color,
    textShadow: '0px 1px 1px rgba(0, 0, 0, 0.08)',
    _hover: {
      bg: hoverBg
    },
    _active: {
      bg: activeBg
    },
    _dark: {
      _disabled: {
        bg: 'black.light'
      }
    }
  };
};

const secondary = (props: StyleFunctionProps): SystemStyleObject => {
  const defaultFillColor = 'silver.lightest';
  const { color, fill = defaultFillColor, theme } = props;
  const isCustomFill = fill !== defaultFillColor;
  const hoverBg = getOneStepDarkerColor(fill);
  const activeBg = getOneStepDarkerColor(hoverBg);

  const darkBg = isCustomFill ? fill : 'black.lighter';
  const darkHoverBg = isCustomFill ? getOneStepLighterColor(darkBg) : getOneStepLighterColor('black.lighter');
  const darkActiveBg = isCustomFill ? getOneStepLighterColor(darkHoverBg) : 'grey.base';

  return {
    bg: fill,
    color: color || pickTextColorBasedOnBgColor(getColor(theme, fill)),
    _hover: {
      bg: hoverBg
    },
    _active: {
      bg: activeBg
    },
    svg: {
      color: color || pickTextColorBasedOnBgColor(getColor(theme, fill))
    },
    _dark: {
      bg: darkBg,
      color: 'silver.lightest',
      _hover: {
        bg: darkHoverBg
      },
      _active: {
        bg: darkActiveBg
      },
      _focus: {
        bg: darkBg,
        _active: {
          bg: darkActiveBg
        }
      },
      svg: {
        color: 'silver.lightest'
      },
      _disabled: {
        bg: 'black.light'
      }
    }
  };
};

const ghostLight = (props: StyleFunctionProps): SystemStyleObject => {
  const { fill = 'black.lighter' } = props;

  return {
    bg: 'transparent',
    color: fill,
    boxShadow: 'none',
    _hover: {
      bg: 'rgba(18, 21, 26, 0.04)',
      _disabled: {
        bg: 'none'
      }
    },
    _active: {
      bg: 'rgba(18, 21, 26, 0.06)'
    },
    _focus: {
      bg: 'rgba(18, 21, 26, 0.04)',
      _active: {
        bg: 'rgba(18, 21, 26, 0.06)'
      }
    },
    _disabled: {
      color: 'grey.light',
      bg: 'none'
    },
    svg: {
      color: fill
    }
  };
};

const ghostDark = (props: StyleFunctionProps): SystemStyleObject => {
  const { fill = 'silver.light' } = props;

  return {
    color: fill,
    bg: 'transparent',
    _hover: {
      bg: 'rgba(255, 255, 255, 0.08)',
      _disabled: {
        bg: 'none'
      }
    },
    _active: {
      bg: 'rgba(255, 255, 255, 0.16);'
    },
    _focus: {
      bg: 'rgba(255, 255, 255, 0.08)',
      _active: {
        bg: 'rgba(255, 255, 255, 0.16);'
      }
    },
    svg: {
      color: fill
    }
  };
};

const ghost = (props: StyleFunctionProps): SystemStyleObject => {
  return {
    ...ghostLight(props),
    _dark: { ...ghostDark(props) }
  };
};

const outline = (props: StyleFunctionProps): SystemStyleObject => {
  const { fill } = props;
  const { r, g, b } = (fill && convertHexToRGB(fill)) || { r: 18, g: 21, b: 26 };
  const border = '1px solid';
  const borderColor = `rgba(${r}, ${g}, ${b}, 0.16)`;
  const { r: dr, g: dg, b: db } = (fill && convertHexToRGB(fill)) || { r: 255, g: 255, b: 255 };
  const darkBorderColor = `rgba(${dr}, ${dg}, ${db}, 0.48)`;

  return {
    border,
    borderColor,
    ...ghostLight(props),
    _disabled: {
      color: 'grey.light',
      bg: 'none',
      border: '1px solid rgba(18, 21, 26, 0.16)'
    },
    _focus: {
      borderColor: 'transparent',
      _active: {
        borderColor: 'transparent'
      }
    },
    _dark: {
      border,
      borderColor: darkBorderColor,
      ...ghostDark(props),
      _disabled: {
        bg: 'none',
        border: '1px solid rgba(255, 255, 255, 0.48)'
      },
      _focus: {
        borderColor: 'transparent',
        _active: {
          borderColor: 'transparent'
        }
      }
    }
  };
};

const ButtonTheme: ComponentSingleStyleConfig = {
  baseStyle,
  sizes: {
    xs: {
      borderRadius: '6px',
      fontSize: 'sm',
      paddingLeft: '8px',
      paddingRight: '8px'
    },
    sm: {
      borderRadius: '8px',
      fontSize: 'md',
      paddingLeft: '12px',
      paddingRight: '12px'
    },
    md: {
      borderRadius: '10px',
      fontSize: 'lg',
      paddingLeft: '16px',
      paddingRight: '16px'
    },
    lg: {
      borderRadius: '12px',
      fontSize: 'xl',
      paddingLeft: '16px',
      paddingRight: '16px'
    }
  },
  variants: {
    primary,
    secondary,
    default: secondary,
    outline,
    ghost
  }
};

export default ButtonTheme;
